import { OneTrustCookieGroup } from '~/assets/constants/cookie-consent';
import { EventConstants } from '~/assets/constants/ecomApi';
import type { Product } from '~/types/ecom/product/product.type';
import { TrolleyChannel } from '~/types/ecom/trolley/trolley-map';
import type { TrolleyLineItem } from '~/types/ecom/trolley/trolley.type';

// Type definitions for event tracking
type ExponeaEventType = string;
type ExponeaEventData = Record<string, any>;

// Function to transform product data
function transformProductData(productData: Product): Record<string, any> {
  return {
    id: productData.code ?? '',
    name: productData.full_name ?? '',
    brand: productData.brand ?? 'Unknown',
    category: productData.taxonomies?.[0]?.map((cat) => cat.name).join('/') ?? '',
    total_price_exc_tax: productData.prices?.raw?.net ?? 0,
    original_price: productData.prices?.was_price_raw ?? 0,
    price: productData.prices?.raw?.gross ?? 0,
    category_level_1: productData.taxonomies?.[0]?.[0]?.name ?? '',
    category_level_2: productData.taxonomies?.[0]?.[1]?.name ?? '',
    category_level_3: productData.taxonomies?.[0]?.[2]?.name ?? '',
    currency: EventConstants.currency,
    image: productData.images?.biggest ?? '',
    out_of_stock_delivery: !productData.next_day_delivery,
    language: useNuxtApp().$i18n.locale.value,
    url: window.location.href,
  };
}

// Exponea event tracking function
export const trackExponeaEvent = (
  eventName: ExponeaEventType,
  eventData: ExponeaEventData = {},
  debug: boolean = false
) => {
  const rootStore = useRootStore();
  /**
   * @description verify cookie consent before triggering exponea tracking
   */
  const checkAnalyticsConsent = rootStore.checkConsent(OneTrustCookieGroup.Analytical);
  if (!checkAnalyticsConsent) return;

  if (import.meta.client) {
    if (window.exponea) {
      if (eventName === 'view_item') {
        const transformedData = transformProductData(eventData as Product);
        window.exponea.track(eventName, transformedData);
      } else {
        window.exponea.track(eventName, eventData);
      }

      if (debug) {
        console.log(` Exponea Event Tracked: ${eventName}`, eventData);
      }
    } else {
      console.warn('Exponea is not available on the client.');
    }
  } else {
    console.warn('Exponea tracking attempted on server. This should only run on the client.');
  }
};
function transformProductDataV2(productData: Product, quantity: number = 1): Record<string, any> {
  return {
    brand: productData.brand ?? 'Unknown',
    category: productData.taxonomies?.[0]?.map((cat) => cat.name) ?? [],
    channel: productData.channel ?? 0, // 1,2
    delivery_method: TrolleyChannel[productData.channel], // Delivery, Directship, Collection, NextDayCollection
    discount_value: productData.prices?.raw?.discount_value ?? 0,
    item_id: productData.code ?? '',
    item_price: productData.prices?.raw?.gross ?? 0,
    item_price_exc_tax: productData.prices?.raw?.net ?? 0,
    quantity: quantity, // Defaulting to 1, adjust if needed
    slug: productData.slug ?? '',
    title: productData.full_name ?? '',
    total_price: productData.prices?.raw?.gross ?? 0,
    total_price_exc_tax: productData.prices?.raw?.net ?? 0,
    trolley_channel: productData.channel ?? 0, // Assuming fixed value
  };
}

export async function postTrolleyUpdate(actionType: string, line: TrolleyLineItem) {
  const { useTrolleyStore } = await import('~/stores/trolley.store');
  const trolleyStore = useTrolleyStore();
  const { product, channel, quantity } = line;
  if (!product) return;
  const transformedDetails = transformProductDataV2({ ...product, channel }, quantity);

  const productList: any[] = [];

  let totalPrice = 0;
  let totalPriceExcTax = 0;
  let totalTax = 0;
  let totalQuantity = 0;

  if (!trolleyStore.trolley_line_items) return;

  Object.entries(trolleyStore.trolley_line_items).forEach(([_channel, items]) => {
    if (items.length > 0) {
      items.forEach((item) => {
        const quantity = item.quantity;
        const netPrice = item.product?.prices?.raw?.net ?? 0;
        const grossPrice = item.product?.prices?.raw?.gross ?? 0;
        const vat = item.product?.prices?.raw?.vat ?? 0;

        totalPrice += grossPrice * quantity;
        totalPriceExcTax += netPrice * quantity;
        totalTax += vat * quantity;
        totalQuantity += quantity;

        productList.push({
          item_id: item.product?.code,
          quantity: item.quantity,
        });
      });
    }
  });

  // Exponea tracking object
  const exponeaTrackingObject = computed(() => ({
    all_products_for_delivery: trolleyStore.trolley.is_all_lines_delivery,
    currency: EventConstants.currency,
    discount_value: trolleyStore.total_discount_amount,
    button: TrolleyChannel[line.channel],
    promo_code_value: 0,
    action: actionType,
    tax_value: totalTax.toFixed(2),
    total_price: totalPrice.toFixed(2),
    total_price_exc_tax: totalPriceExcTax.toFixed(2),
    total_quantity: totalQuantity,
    product_list: productList,
    product_details: transformedDetails,
  }));

  // Exponea tracking
  trackExponeaEvent('cart_update', exponeaTrackingObject.value);
}

export function trackIdentify(userId: string, userData: Record<string, any> = {}): void {
  if (typeof window !== 'undefined') {
    trackExponeaEvent('identify', { customer_id: userId, ...userData });
  }
}

export function trackFirstSession(): void {
  if (typeof window === 'undefined') return;

  if (!localStorage.getItem('exponea_first_session_done')) {
    trackExponeaEvent('first_session', { timestamp: new Date().toISOString() });
    localStorage.setItem('exponea_first_session_done', 'true');
  }
}

export function trackSessionStart(): void {
  if (typeof window === 'undefined') return;

  sessionStorage.setItem('exponea_session_active', 'true');
  trackExponeaEvent('session_start', { timestamp: new Date().toISOString() });
}

export function trackSessionEnd(): void {
  if (typeof window === 'undefined') return;

  if (sessionStorage.getItem('exponea_session_active')) {
    trackExponeaEvent('session_end', { timestamp: new Date().toISOString() });
    sessionStorage.removeItem('exponea_session_active');
  }
}

export function trackPageVisit(path?: string): void {
  if (typeof window === 'undefined') return;

  const url = path || window.location.pathname;
  trackExponeaEvent('page_visit', {
    url,
    referrer: document.referrer,
    title: document.title,
    timestamp: new Date().toISOString(),
  });
}
